import { useEffect, useMemo, useRef, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./ExperienceAiGuide.css";

export type ExperienceGuideNavItem={
  routeKey:string;
  path:string;
  label:string;
  description?:string|null;
};

type Experience="CENTER"|"WALLET";
type GuideRoute={path:string;label:string;reason:string};
type GuideResult={
  mode?:"AI"|"LOCAL_SEARCH";
  configured?:boolean;
  aiAvailable?:boolean;
  model?:string|null;
  answer?:string;
  routes?:GuideRoute[];
  followUps?:string[];
  warnings?:string[];
};

const QUICK:Record<Experience,string[]>={
  CENTER:[
    "오늘 먼저 처리해야 할 업무는?",
    "학생 진로기록은 어디서 확인해?",
    "검사를 학급에 배정하려면?",
    "배지 수령상태는 어디서 봐?",
    "학교 운영 보고서는 어디서 만들어?"
  ],
  WALLET:[
    "지금 내가 해야 할 일은?",
    "내 경험을 기록하려면 어디로 가?",
    "목표와 다음 행동을 만들고 싶어",
    "내 배지와 Life Stamp는 어디 있어?",
    "포트폴리오를 만들고 싶어"
  ]
};

function localMatches(query:string,navigation:ExperienceGuideNavItem[]){
  const needle=query.trim().toLowerCase();
  if(!needle)return [];
  const parts=needle.split(/\s+/).filter((part)=>part.length>=2);
  return navigation.map((item)=>{
    const hay=[item.label,item.description,item.routeKey].filter(Boolean).join(" ").toLowerCase();
    return {item,score:(hay.includes(needle)?5:0)+parts.reduce((sum,part)=>sum+(hay.includes(part)?1:0),0)};
  }).filter((row)=>row.score>0).sort((a,b)=>b.score-a.score).slice(0,5).map((row)=>row.item);
}

export function ExperienceAiGuide({
  client,experience,open,currentPath,workspaceId,navigation,onClose,onNavigate
}:{
  client:PlatformClient;
  experience:Experience;
  open:boolean;
  currentPath:string;
  workspaceId?:string|null;
  navigation:ExperienceGuideNavItem[];
  onClose:()=>void;
  onNavigate:(path:string)=>void;
}){
  const [query,setQuery]=useState("");
  const [result,setResult]=useState<GuideResult|null>(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [configured,setConfigured]=useState<boolean|null>(null);
  const [aiAvailable,setAiAvailable]=useState<boolean|null>(null);
  const [model,setModel]=useState("");
  const inputRef=useRef<HTMLInputElement|null>(null);

  const isCenter=experience==="CENTER";
  const title=isCenter?"AI 진로교사 안내자":"AI 성장 안내자";
  const eyebrow=isCenter?"CENTER TEACHER GUIDE":"LIFE PASSPORT GUIDE";
  const subtitle=isCenter
    ?"업무 위치와 현재 운영상태를 자연어로 찾아드립니다."
    :"내 기록과 다음 행동을 자연어로 찾아드립니다.";
  const placeholder=isCenter
    ?"예: 2학년 검사 배정은 어디서 하지?"
    :"예: 내 포트폴리오는 어디서 만들지?";

  useEffect(()=>{
    if(!open)return;
    inputRef.current?.focus();
    const onKey=(event:KeyboardEvent)=>{if(event.key==="Escape")onClose();};
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[open,onClose]);

  useEffect(()=>{
    let active=true;
    client.functions.invoke("experience-ai-guide",{
      body:{action:"STATUS",experience,workspaceId:workspaceId||null}
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setConfigured(false);setAiAvailable(false);return;}
      const next=(data||{}) as GuideResult;
      setConfigured(Boolean(next.configured));
      setAiAvailable(Boolean(next.aiAvailable));
      setModel(typeof next.model==="string"?next.model:"");
    }).catch(()=>{if(active){setConfigured(false);setAiAvailable(false);}});
    return()=>{active=false;};
  },[client,experience,workspaceId]);

  useEffect(()=>{
    if(!open){setQuery("");setResult(null);setError("");}
  },[open]);

  const instant=useMemo(()=>localMatches(query,navigation),[query,navigation]);

  async function search(nextQuery=query){
    const value=nextQuery.trim();
    if(!value)return;
    setQuery(value);setBusy(true);setError("");setResult(null);
    const {data,error}=await client.functions.invoke("experience-ai-guide",{
      body:{
        action:"SEARCH",
        experience,
        query:value,
        currentPath,
        workspaceId:workspaceId||null,
        navigation
      }
    });
    setBusy(false);
    if(error){
      setError(isCenter?"Center 안내 검색을 완료하지 못했습니다.":"Wallet 안내 검색을 완료하지 못했습니다.");
      return;
    }
    const next=(data||{}) as GuideResult;
    setResult(next);
    if(typeof next.configured==="boolean")setConfigured(next.configured);
    if(typeof next.aiAvailable==="boolean")setAiAvailable(next.aiAvailable);
    if(typeof next.model==="string")setModel(next.model);
  }

  function go(path:string){onNavigate(path);onClose();}
  if(!open)return null;

  return (
    <div className={"experience-ai-layer "+experience.toLowerCase()} role="dialog" aria-modal="true" aria-label={title}>
      <button className="experience-ai-backdrop" aria-label="닫기" onClick={onClose}/>
      <aside className="experience-ai-panel">
        <header className="experience-ai-header">
          <div className="experience-ai-brand">
            <span className="experience-ai-mark">AI</span>
            <div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2><p>{subtitle}</p></div>
          </div>
          <button className="experience-ai-close" onClick={onClose}>×</button>
        </header>

        <div className="experience-ai-status">
          <span className={aiAvailable?"ready":""}>
            {configured===null?"AI 상태 확인 중":aiAvailable?"AI 안내 연결됨":"메뉴 검색 모드"}
          </span>
          {model&&<small>{model}</small>}
        </div>

        <form className="experience-ai-search" onSubmit={(event)=>{event.preventDefault();void search();}}>
          <span>⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(event)=>{setQuery(event.target.value);setResult(null);setError("");}}
            placeholder={placeholder}
          />
          <button disabled={busy||!query.trim()}>{busy?"검색 중…":"검색"}</button>
        </form>

        {!query.trim()&&!result&&(
          <section className="experience-ai-quick">
            <p className="eyebrow">빠른 질문</p>
            <div>{QUICK[experience].map((item)=><button key={item} onClick={()=>void search(item)}>{item}<span>→</span></button>)}</div>
          </section>
        )}

        {query.trim()&&!result&&!busy&&instant.length>0&&(
          <section className="experience-ai-instant">
            <p className="eyebrow">메뉴 바로찾기</p>
            {instant.map((item)=>(
              <button key={item.path} onClick={()=>go(item.path)}>
                <span><strong>{item.label}</strong><small>{item.description||item.routeKey}</small></span><b>이동 →</b>
              </button>
            ))}
          </section>
        )}

        {busy&&(
          <div className="experience-ai-thinking">
            <i/><div><strong>{isCenter?"교사 업무를 찾고 있습니다":"내 기록을 찾고 있습니다"}</strong>
            <span>{isCenter?"현재 메뉴와 접근 가능한 학교 운영정보를 함께 확인합니다.":"현재 메뉴와 내 Life Passport 기록을 함께 확인합니다."}</span></div>
          </div>
        )}

        {error&&<p className="error">{error}</p>}

        {result&&(
          <div className="experience-ai-result">
            <div className="experience-ai-answer">
              <span className="experience-ai-mark small">AI</span><p>{result.answer}</p>
            </div>

            {Array.isArray(result.routes)&&result.routes.length>0&&(
              <section className="experience-ai-routes">
                <p className="eyebrow">바로가기</p>
                {result.routes.map((route)=>(
                  <button key={route.path} onClick={()=>go(route.path)}>
                    <span><strong>{route.label}</strong><small>{route.reason}</small></span><b>열기 →</b>
                  </button>
                ))}
              </section>
            )}

            {Array.isArray(result.warnings)&&result.warnings.length>0&&(
              <div className="experience-ai-warnings">
                <strong>확인 필요</strong>
                {result.warnings.map((item,index)=><span key={index}>{item}</span>)}
              </div>
            )}

            {Array.isArray(result.followUps)&&result.followUps.length>0&&(
              <section className="experience-ai-followups">
                <p className="eyebrow">이어서 물어보기</p>
                <div>{result.followUps.map((item)=><button key={item} onClick={()=>void search(item)}>{item}</button>)}</div>
              </section>
            )}
          </div>
        )}

        <footer className="experience-ai-footer">
          <span>검색·안내 전용</span>
          <p>{isCenter
            ?"학생 정보 변경, 배지 발급, 상담 저장 같은 업무는 교사가 직접 확인하고 실행합니다."
            :"기록 저장, 검사 제출, 공유 같은 행동은 내가 직접 확인하고 실행합니다."}</p>
        </footer>
      </aside>
    </div>
  );
}
